import React, { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Footer } from "@/components/ui/footer";
import { HelmetProvider } from 'react-helmet-async';
import { AppLayout } from "@/components/AppLayout";
import { PrivateLayout } from "@/components/PrivateLayout";
import { AuthGuard } from "@/components/auth/AuthGuard";
import { AntiScrapingGuard } from "@/components/AntiScrapingGuard";
import { FAQChatbot } from "@/components/faq/FAQChatbot";
import { SubdomainRedirect } from "@/components/SubdomainRedirect";
import { PWAInstallBanner } from "@/components/PWAInstallBanner";
import { PWAUpdatePrompt } from "@/components/pwa/PWAUpdatePrompt";
import { NotificationSoundProvider } from "@/components/NotificationSoundProvider";
import { PageLoader } from "@/components/ui/PageLoader";

import { AnalyticsTracker } from "@/components/AnalyticsTracker";
import { ScrollToTop } from "@/components/ScrollToTop";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { lazyWithRetry } from "@/utils/lazyWithRetry";

// Lazy loading de toutes les pages publiques (avec retry automatique)
const Index = lazyWithRetry(() => import("./pages/Index"), "Index");
const MissionsDemandeur = lazyWithRetry(() => import("./pages/MissionsDemandeur"), "MissionsDemandeur");
const Prestataires = lazyWithRetry(() => import("./pages/Prestataires"), "Prestataires");
const CartePrestataires = lazyWithRetry(() => import("./pages/CartePrestataires"), "CartePrestataires");
const Tarifs = lazyWithRetry(() => import("./pages/Tarifs"), "Tarifs");
const Comparatif = lazyWithRetry(() => import("./pages/Comparatif"), "Comparatif");
const CommentCaMarche = lazyWithRetry(() => import("./pages/CommentCaMarche"), "CommentCaMarche");
const APropos = lazyWithRetry(() => import("./pages/APropos"), "APropos");
const DeposerMission = lazyWithRetry(() => import("./pages/DeposerMission"), "DeposerMission");
const Contact = lazyWithRetry(() => import("./pages/Contact"), "Contact");
const ClientOnboarding = lazyWithRetry(() => import("./pages/ClientOnboarding"), "ClientOnboarding");
const PrestataireOnboarding = lazyWithRetry(() => import("./pages/PrestataireOnboarding"), "PrestataireOnboarding");
const AdminManagement = lazyWithRetry(() => import("./pages/AdminManagement"), "AdminManagement");
const Auth = lazyWithRetry(() => import("./pages/Auth"), "Auth");
const ProviderForm = lazyWithRetry(() => import("./pages/ProviderForm"), "ProviderForm");
const NotFound = lazyWithRetry(() => import("./pages/NotFound"), "NotFound");
const ForgotPassword = lazyWithRetry(() => import("./pages/ForgotPassword"), "ForgotPassword");
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"), "ResetPassword");
const RGPD = lazyWithRetry(() => import("./pages/RGPD"), "RGPD");
const CGU = lazyWithRetry(() => import("./pages/CGU"), "CGU");
const CGV = lazyWithRetry(() => import("./pages/CGV"), "CGV");
const ChartePrestataire = lazyWithRetry(() => import("./pages/ChartePrestataire"), "ChartePrestataire");
const MentionsLegales = lazyWithRetry(() => import("./pages/MentionsLegales"), "MentionsLegales");
const Cookies = lazyWithRetry(() => import("./pages/Cookies"), "Cookies");
const Install = lazyWithRetry(() => import("./pages/Install"), "Install");
const Lexique = lazyWithRetry(() => import("./pages/Lexique"), "Lexique");
const FAQPro = lazyWithRetry(() => import("./pages/FAQPro"), "FAQPro");
const PrestataireDetails = lazyWithRetry(() => import("./pages/PrestataireDetails"), "PrestataireDetails");
const SupportAccess = lazyWithRetry(() => import("./pages/SupportAccess"), "SupportAccess");
const MissionResponse = lazyWithRetry(() => import("./pages/MissionResponse"), "MissionResponse");
const ClientMissionSelection = lazyWithRetry(() => import("./pages/ClientMissionSelection"), "ClientMissionSelection");
const PaymentSuccess = lazyWithRetry(() => import("./pages/PaymentSuccess"), "PaymentSuccess");
const PaymentCancelled = lazyWithRetry(() => import("./pages/PaymentCancelled"), "PaymentCancelled");

// Lazy loading des pages client
const DashboardClient = lazyWithRetry(() => import("./pages/DashboardClient"), "DashboardClient");
const RecapMissions = lazyWithRetry(() => import("./pages/client/RecapMissions"), "RecapMissions");
const MesInfosClient = lazyWithRetry(() => import("./pages/client/MesInfos"), "MesInfosClient");
const AddMissionIA = lazyWithRetry(() => import("./pages/client/AddMissionIA"), "AddMissionIA");
const AddMissionTexte = lazyWithRetry(() => import("./pages/client/AddMissionTexte"), "AddMissionTexte");
const EditMission = lazyWithRetry(() => import("./pages/client/EditMission"), "EditMission");
const MotDePasseClient = lazyWithRetry(() => import("./pages/client/MotDePasse"), "MotDePasseClient");
const ParametresClient = lazyWithRetry(() => import("./pages/client/Parametres"), "ParametresClient");
const ClientSupport = lazyWithRetry(() => import("./pages/client/Support"), "ClientSupport");

// Lazy loading des pages prestataire
const DashboardPrestataire = lazyWithRetry(() => import("./pages/DashboardPrestataire"), "DashboardPrestataire");
const MesInfos = lazyWithRetry(() => import("./pages/prestataire/MesInfos"), "MesInfos");
const MesCompetences = lazyWithRetry(() => import("./pages/prestataire/MesCompetences"), "MesCompetences");
const MesContraintes = lazyWithRetry(() => import("./pages/prestataire/MesContraintes"), "MesContraintes");
const MotDePasse = lazyWithRetry(() => import("./pages/prestataire/MotDePasse"), "MotDePasse");
const ParametresPrestataire = lazyWithRetry(() => import("./pages/prestataire/Parametres"), "ParametresPrestataire");
const Calendrier = lazyWithRetry(() => import("./pages/prestataire/Calendrier"), "Calendrier");
const Missions = lazyWithRetry(() => import("./pages/prestataire/Missions"), "Missions");
const ProfilEnrichi = lazyWithRetry(() => import("./pages/prestataire/ProfilEnrichi"), "ProfilEnrichi");
const PrestataireProfilPartenaire = lazyWithRetry(() => import("./pages/prestataire/ProfilPartenaire"), "PrestataireProfilPartenaire");
const EstimationIA = lazyWithRetry(() => import("./pages/prestataire/EstimationIA"), "EstimationIA");
const EstimationGestion = lazyWithRetry(() => import("./pages/prestataire/EstimationGestion"), "EstimationGestion");
const PrestataireSupport = lazyWithRetry(() => import("./pages/prestataire/Support"), "PrestataireSupport");
const ParametresDevis = lazyWithRetry(() => import("./pages/prestataire/ParametresDevis"), "ParametresDevis");
const MesDevis = lazyWithRetry(() => import("./pages/prestataire/MesDevis"), "MesDevis");
const DevisEditor = lazyWithRetry(() => import("./pages/prestataire/DevisEditor"), "DevisEditor");
const MonPortfolio = lazyWithRetry(() => import("./pages/prestataire/MonPortfolio"), "MonPortfolio");
const MesMessages = lazyWithRetry(() => import("./pages/MesMessages"), "MesMessages");
const DashboardAdmin = lazyWithRetry(() => import("./pages/DashboardAdminSimple"), "DashboardAdmin");
const PrestatairesAdmin = lazyWithRetry(() => import("./pages/PrestatairesAdmin"), "PrestatairesAdmin");
const ClientsAdmin = lazyWithRetry(() => import("./pages/ClientsAdmin"), "ClientsAdmin");
const StatsMissions = lazyWithRetry(() => import("./pages/StatsMissions"), "StatsMissions");
const WebhookTest = lazyWithRetry(() => import("./pages/WebhookTest"), "WebhookTest");
const AdminMissions = lazyWithRetry(() => import("./pages/AdminMissions"), "AdminMissions");
const AdminClients = lazyWithRetry(() => import("./pages/AdminClients"), "AdminClients");
const AdminPrestataires = lazyWithRetry(() => import("./pages/AdminPrestataires"), "AdminPrestataires");
const AdminDocuments = lazyWithRetry(() => import("./pages/AdminDocuments"), "AdminDocuments");
const AdminStats = lazyWithRetry(() => import("./pages/AdminStats"), "AdminStats");
const AdminFAQ = lazyWithRetry(() => import("./pages/AdminFAQ"), "AdminFAQ");
const AdminPassword = lazyWithRetry(() => import("./pages/AdminPassword"), "AdminPassword");
const AdminTOTPVerification = lazyWithRetry(() => import("./pages/AdminTOTPVerification"), "AdminTOTPVerification");
const AdminsPage = lazyWithRetry(() => import("./pages/AdminsPage"), "AdminsPage");
const EditPrestataire = lazyWithRetry(() => import("./pages/admin/EditPrestataire"), "EditPrestataire");
const Audio = lazyWithRetry(() => import("./pages/admin/Audio"), "Audio");

const EmbeddingTest = lazyWithRetry(() => import("./pages/admin/EmbeddingTest"), "EmbeddingTest");
const RegenerateEmbeddings = lazyWithRetry(() => import("./pages/admin/RegenerateEmbeddings"), "RegenerateEmbeddings");
const AdminTestModePage = lazyWithRetry(() => import("./pages/AdminTestModePage"), "AdminTestModePage");
const AdminLexique = lazyWithRetry(() => import("./pages/admin/AdminLexique"), "AdminLexique");
const AdvancedAnalytics = lazyWithRetry(() => import("./pages/admin/AdvancedAnalytics"), "AdvancedAnalytics");
const GestionTarifs = lazyWithRetry(() => import("./pages/admin/GestionTarifs"), "GestionTarifs");
const ReferentielBaseAutomatisee = lazyWithRetry(() => import("./pages/admin/ReferentielBaseAutomatisee"), "ReferentielBaseAutomatisee");
const ProfilPartenaire = lazyWithRetry(() => import("./pages/admin/ProfilPartenaire"), "ProfilPartenaire");
const AdminEstimationIA = lazyWithRetry(() => import("./pages/admin/EstimationIA"), "AdminEstimationIA");
const LegalDocuments = lazyWithRetry(() => import("./pages/admin/LegalDocuments"), "LegalDocuments");
const SupportAdmin = lazyWithRetry(() => import("./pages/admin/SupportAdmin"), "SupportAdmin");
const AnnoncesPromo = lazyWithRetry(() => import("./pages/admin/AnnoncesPromo"), "AnnoncesPromo");
const CalendrierAdmin = lazyWithRetry(() => import("./pages/admin/CalendrierAdmin"), "CalendrierAdmin");
const AdminMarketing = lazyWithRetry(() => import("./pages/AdminMarketing"), "AdminMarketing");
const RechercheHistorique = lazyWithRetry(() => import("./pages/admin/RechercheHistorique"), "RechercheHistorique");
const SystemTools = lazyWithRetry(() => import("./pages/admin/SystemTools"), "SystemTools");
const RechercheEntreprises = lazyWithRetry(() => import("./pages/admin/RechercheEntreprises"), "RechercheEntreprises");
const ProspectionNouvellesEntreprises = lazyWithRetry(() => import("./pages/admin/ProspectionNouvellesEntreprises"), "ProspectionNouvellesEntreprises");
const RepresentantsSuspects = lazyWithRetry(() => import("./pages/admin/RepresentantsSuspects"), "RepresentantsSuspects");
const SolvabilityReports = lazyWithRetry(() => import("./pages/admin/SolvabilityReports"), "SolvabilityReports");
const ProspectionApiCosts = lazyWithRetry(() => import("./pages/admin/ProspectionApiCosts"), "ProspectionApiCosts");
const ProspectionTemplates = lazyWithRetry(() => import("./pages/admin/ProspectionTemplates"), "ProspectionTemplates");
const VerificationsSiret = lazyWithRetry(() => import("./pages/admin/VerificationsSiret"), "VerificationsSiret");
const GestionCreditsSolvabilite = lazyWithRetry(() => import("./pages/admin/GestionCreditsSolvabilite"), "GestionCreditsSolvabilite");
const SoftLaunch = lazyWithRetry(() => import("./pages/admin/SoftLaunch"), "SoftLaunch");
const AdminNotifications = lazyWithRetry(() => import("./pages/admin/AdminNotifications"), "AdminNotifications");
const AdminMatchingSettings = lazyWithRetry(() => import("./pages/admin/AdminMatchingSettings"), "AdminMatchingSettings");

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <SubdomainRedirect />
          <AntiScrapingGuard>
            <AnalyticsTracker />
            <FAQChatbot />
            <PWAInstallBanner />
            <PWAUpdatePrompt />
            <NotificationSoundProvider />
            {/* Marker to indicate app has loaded successfully */}
            <div data-app-loaded="true" style={{ display: 'none' }} />
            
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Pages publiques avec AppLayout */}
                <Route path="/" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <Index />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/missions" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <MissionsDemandeur />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/prestataires" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <Prestataires />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/carte" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <CartePrestataires />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/prestataire/:id" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <PrestataireDetails />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/tarifs" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <Tarifs />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/comparatif" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <Comparatif />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/comment-ca-marche" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <CommentCaMarche />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/a-propos" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <APropos />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/contact" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <Contact />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/auth" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <Auth />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/client-onboarding" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <ClientOnboarding />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/prestataire-onboarding" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <PrestataireOnboarding />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/admin-management" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <AdminManagement />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/provider-form" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <ProviderForm />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/reset-password" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <ResetPassword />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/forgot-password" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <ForgotPassword />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/rgpd" element={<RGPD />} />
                <Route path="/cgu" element={<CGU />} />
                <Route path="/cgv" element={<CGV />} />
                <Route path="/charte-prestataire" element={<ChartePrestataire />} />
                <Route path="/mentions-legales" element={<MentionsLegales />} />
                <Route path="/cookies" element={<Cookies />} />
                <Route path="/deposer-mission" element={<DeposerMission />} />
                <Route path="/install" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <Install />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/lexique" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <Lexique />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                <Route path="/faq-pro" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <FAQPro />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
                
                {/* Pages privées avec PrivateLayout */}
                <Route path="/dashboard-client" element={
                  <AuthGuard requireAuth={true} requiredRole="client">
                    <PrivateLayout>
                      <DashboardClient />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/client/recap-missions" element={
                  <AuthGuard requireAuth={true} requiredRole="client">
                    <PrivateLayout>
                      <RecapMissions />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/client/mes-infos" element={
                  <AuthGuard requireAuth={true} requiredRole="client">
                    <PrivateLayout>
                      <MesInfosClient />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/client/add-mission-ia" element={
                  <AuthGuard requireAuth={true} requiredRole="client">
                    <PrivateLayout>
                      <AddMissionIA />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/client/add-mission-texte" element={
                  <AuthGuard requireAuth={true} requiredRole="client">
                    <PrivateLayout>
                      <AddMissionTexte />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/client/edit-mission/:missionId" element={
                  <AuthGuard requireAuth={true} requiredRole="client">
                    <PrivateLayout>
                      <EditMission />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/client/mot-de-passe" element={
                  <AuthGuard requireAuth={true} requiredRole="client">
                    <PrivateLayout>
                      <MotDePasseClient />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/client/parametres" element={
                  <AuthGuard requireAuth={true} requiredRole="client">
                    <PrivateLayout>
                      <ParametresClient />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/dashboard-prestataire" element={
                  <AuthGuard requireAuth={true} requiredRole="prestataire">
                    <DashboardPrestataire />
                  </AuthGuard>
                } />
                
                {/* Prestataire specific pages */}
                <Route path="/prestataire/mes-infos" element={
                  <AuthGuard requireAuth={true} requiredRole="prestataire">
                    <MesInfos />
                  </AuthGuard>
                } />
                <Route path="/prestataire/mes-competences" element={
                  <AuthGuard requireAuth={true} requiredRole="prestataire">
                    <MesCompetences />
                  </AuthGuard>
                } />
                <Route path="/prestataire/mes-contraintes" element={
                  <AuthGuard requireAuth={true} requiredRole="prestataire">
                    <MesContraintes />
                  </AuthGuard>
                } />
                <Route path="/prestataire/mot-de-passe" element={
                  <AuthGuard requireAuth={true} requiredRole="prestataire">
                    <MotDePasse />
                  </AuthGuard>
                } />
                <Route path="/prestataire/parametres" element={
                  <AuthGuard requireAuth={true} requiredRole="prestataire">
                    <ParametresPrestataire />
                  </AuthGuard>
                } />
                <Route path="/prestataire/missions" element={
                  <AuthGuard requireAuth={true} requiredRole="prestataire">
                    <Missions />
                  </AuthGuard>
                } />
                <Route path="/prestataire/profil-enrichi" element={
                  <AuthGuard requireAuth={true} requiredRole="prestataire">
                    <ProfilEnrichi />
                  </AuthGuard>
                } />
                <Route path="/prestataire/calendrier" element={
                  <AuthGuard requireAuth={true} requiredRole="prestataire">
                    <Calendrier />
                  </AuthGuard>
                } />
                <Route path="/prestataire/profil-partenaire" element={
                  <AuthGuard requireAuth={true} requiredRole="prestataire">
                    <PrestataireProfilPartenaire />
                  </AuthGuard>
                } />
                <Route path="/prestataire/portfolio" element={
                  <AuthGuard requireAuth={true} requiredRole="prestataire">
                    <PrivateLayout>
                      <MonPortfolio />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/prestataire/messages" element={
                  <AuthGuard requireAuth={true} requiredRole="prestataire">
                    <PrivateLayout>
                      <MesMessages />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/client/messages" element={
                  <AuthGuard requireAuth={true} requiredRole="client">
                    <PrivateLayout>
                      <MesMessages />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/prestataire/estimation-add" element={
                  <AuthGuard requireAuth={true} requiredRole="prestataire">
                    <PrivateLayout>
                      <EstimationIA />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/prestataire/estimation-gestion" element={
                  <AuthGuard requireAuth={true} requiredRole="prestataire">
                    <PrivateLayout>
                      <EstimationGestion />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/prestataire/parametres-devis" element={
                  <AuthGuard requireAuth={true} requiredRole="prestataire">
                    <PrivateLayout>
                      <ParametresDevis />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/prestataire/mes-devis" element={
                  <AuthGuard requireAuth={true} requiredRole="prestataire">
                    <PrivateLayout>
                      <MesDevis />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/prestataire/devis/:id" element={
                  <AuthGuard requireAuth={true} requiredRole="prestataire">
                    <PrivateLayout>
                      <DevisEditor />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/r/:token" element={<MissionResponse />} />
                <Route path="/mission/:missionId/selection" element={<ClientMissionSelection />} />
                <Route path="/r/paiement-ok" element={<PaymentSuccess />} />
                <Route path="/r/paiement-annule" element={<PaymentCancelled />} />
                <Route path="/dashboard-admin" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <DashboardAdmin />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/prestataires-admin" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <PrestatairesAdmin />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/clients-admin" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <ClientsAdmin />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/statistiques-detaillees" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <StatsMissions />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/webhook-test" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <WebhookTest />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/missions" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <AdminMissions />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/clients" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <AdminClients />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/prestataires" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <AdminPrestataires />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/documents" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <AdminDocuments />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/stats" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <AdminStats />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/soft-launch" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <SoftLaunch />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/faq" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <AdminFAQ />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/password" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <AdminPassword />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/totp-verif" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <AdminTOTPVerification />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/audio" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <Audio />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/notifications" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <AdminNotifications />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/admins" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <AdminsPage />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/edit-prestataire" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <EditPrestataire />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/embedding" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <EmbeddingTest />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/regenerate-embeddings" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <RegenerateEmbeddings />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/lexique" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <AdminLexique />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/test-mode" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <AdminTestModePage />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/legal-documents" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <LegalDocuments />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/analytics" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <AdvancedAnalytics />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/tarifs" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <GestionTarifs />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/profil-partenaire" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <ProfilPartenaire />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/estimation-ia" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <AdminEstimationIA />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/rba" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <ReferentielBaseAutomatisee />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/support" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <SupportAdmin />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/annonces" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <AnnoncesPromo />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/calendrier" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <CalendrierAdmin />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/marketing" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <AdminMarketing />
                    </PrivateLayout>
                  </AuthGuard>
                } />


                <Route path="/admin/recherche-historique" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <RechercheHistorique />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/system-tools" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <SystemTools />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/matching-settings" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <AdminMatchingSettings />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/recherche-entreprises" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <RechercheEntreprises />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/prospection-nouvelles-entreprises" element={
                  <Navigate to="/admin/recherche-entreprises" replace />
                } />
                <Route path="/admin/prospection-api-costs" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <ProspectionApiCosts />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/prospection-templates" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <ProspectionTemplates />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/representants-suspects" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <RepresentantsSuspects />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/verifications-siret" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <VerificationsSiret />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/solvability-reports" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <SolvabilityReports />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/admin/credits-solvabilite" element={
                  <AuthGuard requireAuth={true} requiredRole="admin">
                    <PrivateLayout>
                      <GestionCreditsSolvabilite />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/client/support" element={
                  <AuthGuard requireAuth={true} requiredRole="client">
                    <PrivateLayout>
                      <ClientSupport />
                    </PrivateLayout>
                  </AuthGuard>
                } />
                <Route path="/prestataire/support" element={
                  <AuthGuard requireAuth={true} requiredRole="prestataire">
                    <PrestataireSupport />
                  </AuthGuard>
                } />
                
                {/* Page d'accès externe aux tickets support */}
                <Route path="/support/access" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <SupportAccess />
                      </div>
                    </div>
                  </AppLayout>
                } />
                
                {/* Page 404 */}
                <Route path="*" element={
                  <AppLayout>
                    <div className="min-h-screen flex flex-col">
                      <div className="flex-1">
                        <NotFound />
                      </div>
                      <Footer />
                    </div>
                  </AppLayout>
                } />
              </Routes>
            </Suspense>
          </AntiScrapingGuard>
        </BrowserRouter>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
